/**
 * Centralized Theme Management Utility
 * Handles dark mode switching and theme persistence
 */

import { getApiClient } from './apiClient';
import logger from './logger';

const THEME_STORAGE_KEY = 'besper-theme';
const THEME_ATTRIBUTE = 'data-theme';

// Helper to check if user is logged in
const isUserLoggedIn = async () => {
  try {
    const apiClient = getApiClient();
    const token = await apiClient.getApiAccessToken();
    const isLoggedIn = !!token;
    logger.debug('THEME', 'isUserLoggedIn check', { isLoggedIn, hasToken: !!token });
    return isLoggedIn;
  } catch (error) {
    logger.debug('THEME', 'isUserLoggedIn check failed', { error: error.message });
    return false;
  }
};

/**
 * Get the current theme from localStorage or system preference (synchronous)
 * For logged-in users, theme should be loaded from user document via settingsInitializer
 * @returns {string} 'light' or 'dark'
 */
export const getInitialTheme = () => {
  // Platform-public: always light (no dark mode UI)
  return 'light';
};

/**
 * Get theme from user document (async, for logged-in users)
 * @returns {Promise<string>} Theme preference
 */
export const getThemeFromUserDocument = async () => {
  try {
    const loggedIn = await isUserLoggedIn();
    if (loggedIn) {
      const apiClient = getApiClient();
      const response = await apiClient.get('/v1/preferences/settings');
      if (response.success && (response.settings?.theme ?? response.preferences?.theme)) {
        return response.settings?.theme ?? response.preferences.theme;
      }
    }
  } catch (error) {
    logger.warn('THEME', 'Failed to load theme from user document, falling back', { error: error.message });
  }
  return null;
};

/**
 * Apply theme to the document
 * @param {string} theme - 'light', 'dark', or 'auto'
 * @param {boolean} updateBackend - Whether to update user document (default: true)
 */
export const applyTheme = async (theme, updateBackend = true) => {
  const root = document.documentElement;
  let actualTheme = theme;
  
  // Handle 'auto' theme
  if (theme === 'auto') {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    actualTheme = prefersDark ? 'dark' : 'light';
  }
  
  root.setAttribute(THEME_ATTRIBUTE, actualTheme);
  
  let backendUpdated = false;
  
  // Update backend if user is logged in, otherwise use localStorage
  if (updateBackend) {
    const loggedIn = await isUserLoggedIn();
    if (loggedIn) {
      try {
        const apiClient = getApiClient();
        const response = await apiClient.put('/v1/preferences/settings', {
          settings: { theme: theme }
        });
        if (response && response.success) {
          backendUpdated = true;
          logger.debug('THEME', 'Theme updated in user document via API', { theme });
        } else {
          logger.warn('THEME', 'Theme API call returned unsuccessful response', { response });
          // Fall back to localStorage on error
          localStorage.setItem(THEME_STORAGE_KEY, theme);
        }
      } catch (error) {
        logger.error('THEME', 'Failed to update theme in user document', { error: error.message, theme });
        // Fall back to localStorage on error
        localStorage.setItem(THEME_STORAGE_KEY, theme);
      }
    } else {
      // Not logged in - use localStorage
      logger.debug('THEME', 'User not logged in, storing theme in localStorage', { theme });
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  } else {
    // Don't persist (e.g., during initialization)
  }
  
  // Dispatch custom event for theme change
  const event = new CustomEvent('themeChanged', { 
    detail: { theme: actualTheme, preference: theme, isDark: actualTheme === 'dark' } 
  });
  document.dispatchEvent(event);
  
  return { success: true, backendUpdated };
};

/**
 * Get the current theme
 * @returns {string} 'light' or 'dark'
 */
export const getCurrentTheme = () => {
  return document.documentElement.getAttribute(THEME_ATTRIBUTE) || 'light';
};

/**
 * Toggle between light and dark themes
 * @returns {Promise<string>} The new theme
 */
export const toggleTheme = async () => {
  const currentTheme = getCurrentTheme();
  const newTheme = currentTheme === 'light' ? 'dark' : 'light';
  await applyTheme(newTheme);
  return newTheme;
};

/**
 * Initialize theme on app load
 */
export const initializeTheme = () => {
  const theme = getInitialTheme();
  applyTheme(theme);
  
  // Listen for system theme changes (only when user chose "auto")
  if (window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      if (localStorage.getItem(THEME_STORAGE_KEY) === 'auto') {
        applyTheme(e.matches ? 'dark' : 'light');
      }
    });
  }
};

/**
 * Check if dark mode is currently active
 * @returns {boolean}
 */
export const isDarkMode = () => {
  return getCurrentTheme() === 'dark';
};
